import { Input } from "antd";
import type { LucideIcon } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";

import { EllipsisTooltip } from "@/components/TableCells";
import { cn } from "@/lib/cn";
import { emptyPlaceholder } from "@/utils";

/** EditableDetailInfoRow 入参 */
export interface EditableDetailInfoRowProps {
  /** 字段 key，用于双击激活 */
  fieldKey?: string;
  icon: LucideIcon;
  label: string;
  value?: string | number | null | ReactNode;
  mono?: boolean; // 是否为等宽字体
  full?: boolean; // 是否占据两列
  /** 是否处于编辑模式 */
  editMode?: boolean; // 是否处于编辑模式
  /** 该字段是否允许编辑 */
  editable?: boolean; // 是否允许编辑
  /** 当前激活编辑的字段 */
  activeField?: string | null; // 当前激活编辑的字段
  /** 草稿值（字符串字段） */
  draftValue?: string; // 草稿值（字符串字段）
  onActivate?: (fieldKey: string) => void;
  onDeactivate?: () => void; // 取消编辑
  onDraftChange?: (fieldKey: string, value: string) => void; // 修改草稿值
}

/** 单行字段：图标 + 标签 + 值；编辑模式下可双击进入输入 */
export const EditableDetailInfoRow = ({
  fieldKey,
  icon: Icon,
  label,
  value,
  mono,
  full,
  editMode = false,
  editable = false,
  activeField,
  draftValue = "",
  onActivate,
  onDeactivate,
  onDraftChange,
}: EditableDetailInfoRowProps) => {
  /** 当前行是否处于输入态 */
  const isEditing = editMode && editable && fieldKey && activeField === fieldKey;

  /** 回车确认、Esc 取消编辑 */
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onDeactivate?.();
    }
    if (event.key === "Escape") {
      onDeactivate?.();
    }
  };

  if (isEditing && fieldKey) {
    return (
      <div className={cn("flex items-center gap-1.5 text-sm min-w-0", full && "col-span-2")}>
        <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="text-slate-500 w-24 shrink-0">{label}</span>
        <Input
          size="small"
          className={cn("flex-1 min-w-0", mono && "font-mono text-xs")}
          value={draftValue}
          autoFocus
          onChange={(e) => onDraftChange?.(fieldKey, e.target.value)}
          onBlur={() => onDeactivate?.()}
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  /** 可编辑且处于编辑模式：双击激活 */
  const handleDoubleClick = () => {
    if (!editMode || !editable || !fieldKey) return;
    onActivate?.(fieldKey);
  };

  // 展示文案：编辑态优先草稿，否则用原值
  const displayValue = emptyPlaceholder(editMode && editable ? draftValue || value : value);
  // Tooltip 仅支持纯文本；ReactNode 交由 EllipsisTooltip 自行取 children
  const tooltipTitle =
    typeof displayValue === "string" || typeof displayValue === "number"
      ? String(displayValue)
      : undefined;

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 text-sm min-w-0",
        full && "col-span-2",
        editMode && editable && "rounded px-1 -mx-1 hover:bg-orange-50/60 cursor-text",
      )}
      onDoubleClick={handleDoubleClick}
      title={editMode && editable ? "双击编辑" : undefined}
    >
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="text-slate-500 w-24 shrink-0">{label}</span>
      <div className="flex-1 min-w-0">
        <EllipsisTooltip
          title={tooltipTitle}
          className={cn("w-full text-slate-800", mono && "font-mono text-xs")}
        >
          {displayValue}
        </EllipsisTooltip>
      </div>
    </div>
  );
};

/** 单行字段：图标 + 标签 + 值（只读） */
export const DetailInfoRow = ({
  icon: Icon,
  label,
  value,
  mono,
  full,
}: {
  icon: LucideIcon;
  label: string;
  value?: string | number | null | ReactNode;
  mono?: boolean;
  full?: boolean;
}) => <EditableDetailInfoRow icon={Icon} label={label} value={value} mono={mono} full={full} />;
